import { useState } from 'react';
import { ResponseOption } from '../../../../types/User';
import { updateCurrentUserInfo } from '../../../../services/auth';
import OraSwitch from '../../../CustomMUI/Switch';
import { RootState } from '../../../../store/store';
import { useSelector } from 'react-redux';
import InputLabel from '@mui/material/InputLabel';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Slider from '@mui/material/Slider';
import { enqueueSnackbar } from 'notistack';

const FlashcardSettings: React.FC = () => {
  const currentUser = useSelector((state: RootState) => state.user.profile);

  const [responseOptions, setResponseOptions] = useState(
    currentUser?.responseOptions || 'two'
  );
  const [flashcardTextSize, setFlashcardTextSize] = useState(
    currentUser?.flashcardTextSize || '1.25'
  );
  const [showIntervals, setShowIntervals] = useState(
    currentUser?.showIntervals || false
  );

  const handleResponseOptionChange = (
    _event: React.MouseEvent<HTMLElement>,
    newResponseOptions: ResponseOption
  ) => {
    if (newResponseOptions !== null) {
      setResponseOptions(newResponseOptions);
      updateResponseOptions(newResponseOptions);
    }
  };

  const updateResponseOptions = async (newResponseOptions: ResponseOption) => {
    await updateCurrentUserInfo({
      responseOptions: newResponseOptions,
    });
  };

  const handleTextSizeChange = (_event: Event, newTextSize: string) => {
    if (newTextSize !== null) {
      setFlashcardTextSize(newTextSize);
    }
  };

  const handleTextSizeChangeCommitted = (
    _event: Event | React.SyntheticEvent,
    newTextSize: number | number[]
  ) => {
    if (newTextSize !== null) {
      updateTextSize(newTextSize.toString());
    }
  };

  const updateTextSize = async (newSize: string) => {
    await updateCurrentUserInfo({
      flashcardTextSize: newSize,
    });
    enqueueSnackbar('Text size updated.', {
      autoHideDuration: 3000,
    });
  };

  const handleShowIntervalsChange = (showIntervals: boolean) => {
    setShowIntervals(showIntervals);
    updateShowIntervals(showIntervals);
  };

  const updateShowIntervals = async (showIntervals: boolean) => {
    await updateCurrentUserInfo({
      showIntervals: showIntervals,
    });
  };

  const marks = [
    {
      value: 1,
      label: 'Small',
    },
    {
      value: 1.25,
      label: 'Default',
    },
    {
      value: 1.5,
      label: 'Large',
    },
  ];

  return (
    <>
      <div className="control control--intervals">
        <InputLabel>Show Intervals</InputLabel>
        <OraSwitch
          checked={showIntervals}
          onChange={(e) => handleShowIntervalsChange(e.target.checked)}
        />
      </div>
      <div className="control control--response-options">
        <InputLabel className="m-b-0-50">Response Options</InputLabel>
        <ToggleButtonGroup
          value={responseOptions}
          exclusive
          onChange={handleResponseOptionChange}
          aria-label="response options"
          sx={{ width: '100%' }}
        >
          <ToggleButton value="two" aria-label="2 response">
            Two Buttons
          </ToggleButton>
          <ToggleButton value="four" aria-label="4 response">
            Four Buttons
          </ToggleButton>
        </ToggleButtonGroup>
      </div>
      <div className="control control--text-size m-t-1-50">
        <InputLabel className="m-b-0-50">Text Size</InputLabel>
        <Slider
          aria-label="Font Size"
          value={Number(flashcardTextSize)}
          onChange={(e, newTextSize) =>
            handleTextSizeChange(e, newTextSize.toString())
          }
          onChangeCommitted={handleTextSizeChangeCommitted}
          marks={marks}
          step={0.05}
          min={0.75}
          max={1.75}
          valueLabelDisplay="auto"
          valueLabelFormat={(value) => `${(value * 16).toFixed(1)}px`}
          sx={{ width: '100%' }}
        />
      </div>
    </>
  );
};

export default FlashcardSettings;
